import { Value, Label, Expr } from './ast'
import { Stack } from './stack'
import { Path, line, join, close } from '../api/path'
import { Shape, union, intersection, difference } from '../api/shape'
import { group } from '../api/group'
import { Interpreter } from './interpreter'
import { cellID } from './cell'
import { cons, reverse, toPt } from './list'

const primitives: Map<string, (stack: Stack, interp: Interpreter) => void> = new Map();

class Primitives {
    operator(name: string, stack: Stack, interp: Interpreter): void {
        if (!this.word(name, stack, interp))
            throw new Error("Unknown operator: " + name);
    }

    word(name: string, stack: Stack, interp: Interpreter): boolean {
        if (primitives.has(name)) {
            primitives.get(name)(stack, interp);
            return true;
        }

        return false;
    }
}

function prim(name: string, fn: (stack: Stack, interp: Interpreter) => void) {
    primitives.set(name, fn);
}

prim("scale", (stack) => {
    const factor = stack.popNumber();
    const drawing = stack.popDrawing();
    stack.pushDrawing(drawing.scale(factor));
});

prim("rotate", (stack) => {
    const degrees = stack.popNumber();
    const drawing = stack.popDrawing();
    stack.pushDrawing(drawing.rotate(degrees));
});

prim("join", (stack) => {
    const path2 = <Path>stack.popDrawing();
    const path1 = <Path>stack.popDrawing();
    stack.pushDrawing(join([path1, path2]));
})

prim("group", (stack) => {
    const d1 = stack.popDrawing();
    const d2 = stack.popDrawing();
    stack.pushDrawing(group([d1, d2]));
})

prim("close", (stack) => {
    const p1 = <Path>stack.popDrawing();
    stack.pushDrawing(close(p1));
})

prim("anchor", (stack) => {
    const index = stack.pop();
    const d1 = stack.popDrawing();
    switch (index.type) {
        case "number":
            stack.pushDrawing(d1.anchorTo(index.value));
            break;
        case "symbol":
            stack.pushDrawing(d1.anchorTo(index.name));
            break;
        case "list":
            const pt = toPt(index);
            stack.pushDrawing(d1.anchorAt(pt));
            break;
        default:
            stack.expected("number, symbol or list", index);
    }
})

prim("tag", (stack) => {
    const sym = stack.popSymbol();
    const d1 = stack.popDrawing();
    stack.pushDrawing(d1.labelAnchor(sym));
})

prim("color", (stack) => {
    const sym = stack.popSymbol();
    const d1 = stack.popDrawing();
    stack.pushDrawing(d1.stroke(sym));
})

prim("union", (stack) => {
    const d2 = <Shape>stack.popDrawing();
    const d1 = <Shape>stack.popDrawing();
    stack.pushDrawing(union(d1, d2));
})

prim("intersection", (stack) => {
    const d2 = <Shape>stack.popDrawing();
    const d1 = <Shape>stack.popDrawing();
    stack.pushDrawing(intersection(d1, d2));
})

prim("difference", (stack) => {
    const d2 = <Shape>stack.popDrawing();
    const d1 = <Shape>stack.popDrawing();
    stack.pushDrawing(difference(d1, d2));
})

prim("+", (stack) => {
    const n2 = stack.popNumber();
    const n1 = stack.popNumber();
    stack.pushNumber(n1 + n2);
})

prim("-", (stack) => {
    const n2 = stack.popNumber();
    const n1 = stack.popNumber();
    stack.pushNumber(n1 - n2);
})

prim("/", (stack) => {
    const n2 = stack.popNumber();
    const n1 = stack.popNumber();
    stack.pushNumber(n1 / n2);
})

prim("*", (stack) => {
    const n2 = stack.popNumber();
    const n1 = stack.popNumber();
    stack.pushNumber(n1 * n2);
})

prim("line", (stack) => {
    stack.pushDrawing(line(1))
})

prim("dup", (stack) => {
    const v = stack.pop();
    stack.items.push(v);
    stack.items.push(v);
})

prim("drop", (stack) => {
    stack.pop();
})

prim("swap", (stack) => {
    const a = stack.pop();
    const b = stack.pop();
    stack.items.push(a);
    stack.items.push(b);
})

prim("def", (stack, interp) => {
    const sym = stack.popSymbol()
    const val: Label = {
        type: "label",
        name: sym,
        value: stack.pop()
    }
    interp.store(sym, val)
    stack.items.push(val);
})

prim("^", (stack, interp) => {
    if (interp.row == 0) {
        throw new Error("no cell above " + cellID(interp.row, interp.col));
    }
    const value = interp.lookupCell(interp.row - 1, interp.col);
    stack.items.push(value);
})

prim("<", (stack, interp) => {
    if (interp.col == 0) {
        throw new Error("no cell left of " + cellID(interp.row, interp.col));
    }
    const value = interp.lookupCell(interp.row, interp.col - 1);
    stack.items.push(value);
})

prim("fill", (stack, interp) => {
    let rest = stack.pop();
    let first = stack.pop();
    const count = stack.popNumber();
    fill(first, 1, Math.min(1, count), interp);
    fill(rest, 2, count, interp);
    stack.pushNumber(count);
})

prim("(", (stack) => {
    stack.pushLiteral({ type: "null" });
})

prim(",", (stack) => {
    const head = stack.pop();
    const tail = stack.popListOrNull();
    stack.pushLiteral(cons(head, tail));
})

prim(")", (stack) => {
    const head = stack.pop();
    if (head.type == "null")
        stack.pushLiteral(head);
    else {
        const tail = stack.popListOrNull();
        stack.pushLiteral(reverse(cons(head, tail), { type: "null" }));
    }
})

function fill(value: Value, from: number, to: number, interp: Interpreter): void {
    let code: Expr[];
    if (value.type == "block")
        code = value.code;
    else
        code = [value];

    for (let i = from; i <= to; i++) {
        let row = interp.row + i;
        if (interp.code[row][interp.col].length > 0) continue;
        interp.code[row][interp.col] = code;
    }
}

export { Primitives }